import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, type ComponentType, type ReactNode } from "react";
import {
  LayoutDashboard,
  Users,
  AlertTriangle,
  Mail,
  GraduationCap,
  Link2,
  FileText,
  Calendar as CalendarIcon,
  Gift,
  Folder,
  TrendingUp,
  DollarSign,
  Sparkles,
  ChevronRight,
  Plus,
  Search,
  Copy,
  Pencil,
  Archive,
  Trash2,
  BarChart3,
  BookOpen,
  Eye,
  CheckCircle2,
  Info,
  Mic,
  Megaphone,
  Send,
  Sidebar as SidebarIcon,
  Moon,
  Sun,
  Bell,
  ArrowRight,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Wordmark } from "@/components/wordmark";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";
import { getCourseCover } from "@/lib/course-covers";
import { useTheme } from "@/lib/theme";

export const Route = createFileRoute("/demo")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Live Demo, Super Affiliate OS" },
      {
        name: "description",
        content:
          "Walk through every screen of Super Affiliate OS with real-feeling data, Command Center, Anomalies, Digests, Affiliate Centers, Bonus Builder, and more.",
      },
      { property: "og:title", content: "Live Demo, Super Affiliate OS" },
      {
        property: "og:description",
        content:
          "Tour the full product: Command Center, People, Anomalies, Digests, Training, Affiliate Centers, Swipe Files, Calendar, Bonus Builder.",
      },
    ],
  }),
  component: DemoPage,
});

/* ============================================================
 * DATA
 * ============================================================ */

type WorkspaceKey = "prime_mover" | "cf2" | "cf_classic" | "offerlab";

const WORKSPACES: Array<{
  key: WorkspaceKey;
  name: string;
  revenue: number;
  mrr: number;
  oneTime: number;
  activeMonthly: number;
  totalActive: number;
  contacts: number;
  newCustomers: number;
  freeTrials: number;
  upgrades: number;
  upgradeRate: number;
  atRisk: number;
  cancelledTrials: number;
  trialChurned: number;
  churnRate: number;
  secondTierMrr: number;
  secondTierAffiliates: number;
}> = [
  {
    key: "prime_mover",
    name: "Prime Mover",
    revenue: 127400,
    mrr: 14200,
    oneTime: 38640,
    activeMonthly: 487,
    totalActive: 487,
    contacts: 1840,
    newCustomers: 142,
    freeTrials: 38,
    upgrades: 64,
    upgradeRate: 28.4,
    atRisk: 12,
    cancelledTrials: 9,
    trialChurned: 14,
    churnRate: 2.1,
    secondTierMrr: 3420,
    secondTierAffiliates: 47,
  },
  {
    key: "cf2",
    name: "ClickFunnels 2.0",
    revenue: 89650,
    mrr: 9800,
    oneTime: 24180,
    activeMonthly: 312,
    totalActive: 312,
    contacts: 1265,
    newCustomers: 96,
    freeTrials: 24,
    upgrades: 41,
    upgradeRate: 22.6,
    atRisk: 8,
    cancelledTrials: 6,
    trialChurned: 11,
    churnRate: 2.6,
    secondTierMrr: 1980,
    secondTierAffiliates: 31,
  },
  {
    key: "cf_classic",
    name: "ClickFunnels Classic",
    revenue: 42180,
    mrr: 4650,
    oneTime: 11420,
    activeMonthly: 198,
    totalActive: 198,
    contacts: 742,
    newCustomers: 47,
    freeTrials: 12,
    upgrades: 18,
    upgradeRate: 17.9,
    atRisk: 5,
    cancelledTrials: 4,
    trialChurned: 6,
    churnRate: 3.0,
    secondTierMrr: 890,
    secondTierAffiliates: 14,
  },
  {
    key: "offerlab",
    name: "OfferLab",
    revenue: 58920,
    mrr: 6200,
    oneTime: 15180,
    activeMonthly: 156,
    totalActive: 156,
    contacts: 612,
    newCustomers: 68,
    freeTrials: 19,
    upgrades: 27,
    upgradeRate: 24.1,
    atRisk: 4,
    cancelledTrials: 3,
    trialChurned: 7,
    churnRate: 1.9,
    secondTierMrr: 1240,
    secondTierAffiliates: 22,
  },
];

const TOTAL = WORKSPACES.reduce(
  (acc, w) => ({
    revenue: acc.revenue + w.revenue,
    mrr: acc.mrr + w.mrr,
    oneTime: acc.oneTime + w.oneTime,
    activeMonthly: acc.activeMonthly + w.activeMonthly,
    newCustomers: acc.newCustomers + w.newCustomers,
    atRisk: acc.atRisk + w.atRisk,
  }),
  { revenue: 0, mrr: 0, oneTime: 0, activeMonthly: 0, newCustomers: 0, atRisk: 0 },
);
// Dedupe: assume ~6% overlap across programs
const DEDUPED_ACTIVE = Math.round(TOTAL.activeMonthly * 0.94);
const NET_NEW_REVENUE = Math.round(TOTAL.revenue * 0.18);
const CHURN_RATE = 2.4;

const TOP_CUSTOMERS = [
  { name: "Bruce Wayne", email: "bruce@wayneenterprises.io", program: "Prime Mover", ltv: 24890 },
  { name: "Tony Stark", email: "tony@arc-reactor.fund", program: "ClickFunnels 2.0", ltv: 19450 },
  { name: "Sherlock Holmes", email: "sherlock@221b.uk", program: "OfferLab", ltv: 17820 },
  { name: "Hermione Granger", email: "hermione@spew.org", program: "Prime Mover", ltv: 14990 },
  { name: "Walter White", email: "heisenberg@graymatter.co", program: "ClickFunnels 2.0", ltv: 12480 },
  { name: "Leslie Knope", email: "leslie@pawnee.gov", program: "Prime Mover", ltv: 11420 },
  { name: "Michael Scott", email: "worlds.best.boss@dundermifflin.com", program: "ClickFunnels Classic", ltv: 9890 },
  { name: "Ron Swanson", email: "ron@meat.is.life", program: "OfferLab", ltv: 9420 },
];

const GLOBAL_PEOPLE = [
  { name: "Bruce Wayne", email: "bruce@wayneenterprises.io", programs: ["Prime Mover", "ClickFunnels 2.0"], ltv: 24890, last: "5/18/2026", risk: "low" },
  { name: "Tony Stark", email: "tony@arc-reactor.fund", programs: ["ClickFunnels 2.0"], ltv: 19450, last: "5/19/2026", risk: "low" },
  { name: "Sherlock Holmes", email: "sherlock@221b.uk", programs: ["OfferLab"], ltv: 17820, last: "5/17/2026", risk: "low" },
  { name: "Hermione Granger", email: "hermione@spew.org", programs: ["Prime Mover"], ltv: 14990, last: "5/19/2026", risk: "low" },
  { name: "Walter White", email: "heisenberg@graymatter.co", programs: ["ClickFunnels 2.0", "OfferLab"], ltv: 12480, last: "5/16/2026", risk: "medium" },
  { name: "Leslie Knope", email: "leslie@pawnee.gov", programs: ["Prime Mover"], ltv: 11420, last: "5/19/2026", risk: "low" },
  { name: "Michael Scott", email: "worlds.best.boss@dundermifflin.com", programs: ["ClickFunnels Classic"], ltv: 9890, last: "5/15/2026", risk: "low" },
  { name: "Ron Swanson", email: "ron@meat.is.life", programs: ["OfferLab"], ltv: 9420, last: "5/14/2026", risk: "low" },
  { name: "Dwight Schrute", email: "assistant.regional.manager@beets.farm", programs: ["ClickFunnels Classic"], ltv: 8720, last: "5/13/2026", risk: "low" },
  { name: "Tyrion Lannister", email: "drinks.and.knows@casterly.rock", programs: ["Prime Mover"], ltv: 8480, last: "5/19/2026", risk: "low" },
  { name: "Luke Skywalker", email: "luke@tatooine-moisture.farm", programs: ["ClickFunnels 2.0"], ltv: 7920, last: "5/18/2026", risk: "low" },
  { name: "Daenerys Targaryen", email: "mother.of.dragons@dracarys.gg", programs: ["OfferLab"], ltv: 7640, last: "5/12/2026", risk: "high" },
  { name: "Indiana Jones", email: "snakes.no.thanks@archaeology.edu", programs: ["Prime Mover"], ltv: 7420, last: "5/19/2026", risk: "low" },
  { name: "Marty McFly", email: "marty@88mph.co", programs: ["ClickFunnels 2.0"], ltv: 6990, last: "5/17/2026", risk: "low" },
  { name: "Wednesday Addams", email: "wednesday@nevermore.academy", programs: ["OfferLab"], ltv: 6840, last: "5/18/2026", risk: "low" },
  { name: "Hannibal Lecter", email: "fine.dining@quidproquo.it", programs: ["ClickFunnels Classic"], ltv: 6420, last: "5/11/2026", risk: "medium" },
  { name: "Beyoncé Knowles", email: "queen.bey@singlel4dies.com", programs: ["Prime Mover"], ltv: 6180, last: "5/19/2026", risk: "low" },
  { name: "Elon Musk", email: "elon@rockets-and-cars.xyz", programs: ["ClickFunnels 2.0", "OfferLab"], ltv: 5990, last: "5/16/2026", risk: "low" },
  { name: "Oprah Winfrey", email: "you-get-a-course@oprah.tv", programs: ["Prime Mover"], ltv: 5840, last: "5/19/2026", risk: "low" },
  { name: "Snoop Dogg", email: "fo-shizzle@dogghouse.la", programs: ["OfferLab"], ltv: 5680, last: "5/18/2026", risk: "low" },
];
const TOTAL_PEOPLE = 1284;

const ANOMALIES = [
  {
    severity: "critical",
    workspace: "Prime Mover",
    type: "Payment Failure Cluster",
    contacts: 0,
    age: "10h ago",
    title: "24 customers have failed payments, 27.0% of active base at risk",
    body: "More than a quarter of your active recurring base for Prime Mover is currently in a failed payment state. This represents a substantial hit to your next commission payout if these accounts churn out before recovery.",
  },
  {
    severity: "high",
    workspace: "Prime Mover",
    type: "Metric Sanity Drift",
    contacts: 0,
    age: "10h ago",
    title: "1 recurring product tier(s) have no payment_cadence_days set. MRR or renewal projection for these can fall back to a 30-day default, which may be wrong.",
    body: "The system is currently defaulting to a 30-day billing cycle for the 'AI Secrets Challenge Platinum' tier because the exact payment cadence is missing from the data feed.",
  },
  {
    severity: "medium",
    workspace: "ClickFunnels 2.0",
    type: "False Churn Upgrade",
    contacts: 1,
    age: "2d ago",
    title: "Detected false_churn_upgrade: Commission Tracking Discrepancy",
    body: "A cohort of your referrals has triggered a 'churn' event immediately followed by an 'upgrade' event. In the ClickFunnels 2.0 affiliate ecosystem, this often signals a configuration change or a manual plan switch.",
  },
  {
    severity: "medium",
    workspace: "ClickFunnels 2.0",
    type: "Tier Downgrade",
    contacts: 1,
    age: "2d ago",
    title: "Detected tier_downgrade",
    body: "This indicates a shift in your referred customer base toward lower-priced subscription plans. While these users are successfully billed, the immediate impact is a reduction in your monthly recurring commission per head.",
  },
  {
    severity: "medium",
    workspace: "Prime Mover",
    type: "False Churn Upgrade",
    contacts: 46,
    age: "10h ago",
    title: "Detected false_churn_upgrade",
    body: "A recurring referral appears to have churned, but the data indicates the user likely upgraded or transitioned to a higher-tier subscription within the Prime Mover program. Standard reporting might flag this as a loss; it typically signals a higher commission floor for you moving forward.",
  },
  {
    severity: "low",
    workspace: "OfferLab",
    type: "New Customer Surge",
    contacts: 18,
    age: "1d ago",
    title: "OfferLab signups up 218% week-over-week",
    body: "Your OfferLab referral link has generated 18 new active customers in the last 7 days, well above the 30-day rolling average. Likely tied to the May launch promo.",
  },
];

const DIGESTS = [
  {
    period: "Daily",
    age: "Today",
    headline: "Today: 1 open anomaly. Prime Mover MRR holding at $14,200.",
    body: "A critical payment failure event is being monitored in the Prime Mover workspace, 24 customers currently have failed payments, representing 27% of the active base. Across all four workspaces you generated $4,890 in revenue in the last 24 hours, with 11 new active customers (Prime Mover +6, ClickFunnels 2.0 +3, OfferLab +2). Top mover today: OfferLab launch promo continues to convert at 18.4%.",
  },
  {
    period: "Daily",
    age: "Yesterday",
    headline: "Yesterday: $6,120 revenue across all programs. 0 critical anomalies.",
    body: "Strong day across the board. Prime Mover led with $2,840 in commission revenue, followed by ClickFunnels 2.0 at $1,690. 14 new customers, 0 critical issues. Weekly trial-to-paid conversion ticked up to 67%.",
  },
  {
    period: "Weekly",
    age: "3 days ago",
    headline: "Week of May 11–17: $48,210 total revenue (+12.4% vs prior week).",
    body: "All four workspaces grew week-over-week. Prime Mover continues to be the largest contributor at $21,640 (+8.1%). OfferLab posted the largest percentage gain at +34% on the back of the May launch. 4 anomalies opened, 6 resolved. Net new MRR: +$2,180.",
  },
];

const TRAINING_LIBRARY = [
  {
    title: "Super Affiliate OS Setup",
    blurb: "Start here to get step by step tutorials to set up your Affiliate Centers and import all of your data.",
    lessons: 8,
    completed: 3,
    accent: "from-indigo-500/30 via-violet-500/20 to-fuchsia-500/30",
    icon: BookOpen,
  },
  {
    title: "Affiliate Foundations",
    blurb: "Start here, the essentials of running a profitable affiliate practice.",
    lessons: 12,
    completed: 1,
    accent: "from-emerald-500/30 via-teal-500/20 to-cyan-500/30",
    icon: GraduationCap,
  },
  {
    title: "7-Figure Super Affiliate",
    blurb: "The playbook from a $1M+ affiliate. Positioning, list-building, bonus stacks, and the long-game systems behind the biggest commission checks.",
    lessons: 18,
    completed: 0,
    accent: "from-amber-500/30 via-orange-500/20 to-rose-500/30",
    icon: TrendingUp,
  },
];

const MY_TRAINING = [
  {
    title: "Email Marketing Mastery for Affiliates",
    blurb: "Build a list that prints commissions. Welcome sequences, promo cadence, and re-engagement that actually re-engages.",
    lessons: 14,
    completed: 5,
    accent: "from-sky-500/30 via-blue-500/20 to-indigo-500/30",
    icon: Mail,
  },
  {
    title: "Paid Traffic for Promo Launches",
    blurb: "Meta + YouTube playbook for warming cold traffic into JV-launch buyers without burning your account.",
    lessons: 9,
    completed: 2,
    accent: "from-rose-500/30 via-pink-500/20 to-fuchsia-500/30",
    icon: Megaphone,
  },
  {
    title: "Webinar Funnels That Convert at 12%+",
    blurb: "Slide structures, registration pages, and follow-up sequences for high-ticket affiliate webinars.",
    lessons: 11,
    completed: 0,
    accent: "from-violet-500/30 via-purple-500/20 to-indigo-500/30",
    icon: Mic,
  },
];

const AFFILIATE_CENTERS = [
  { name: "Prime Mover Center", workspace: "Prime Mover", opens: 1842, logo: "https://www.google.com/s2/favicons?domain=www.primemover.com&sz=128" },
  { name: "ClickFunnels 2.0 Hub", workspace: "ClickFunnels 2.0", opens: 1216, logo: "https://www.google.com/s2/favicons?domain=www.clickfunnels.com&sz=128" },
  { name: "ClickFunnels Classic", workspace: "ClickFunnels Classic", opens: 684, logo: "https://cdn.worldvectorlogo.com/logos/click-funnels.svg" },
  { name: "OfferLab Center", workspace: "OfferLab", opens: 947, logo: "https://www.google.com/s2/favicons?domain=app.offerlab.com&sz=128" },
];

const PROMO_LINKS = [
  { name: "OfferLab Referral Link", url: "https://app.offerlab.com/join/CAC3gyP5mudKzEWXB", workspace: "OfferLab", clicks: 4218, unique: 3104, epc: 4.82, revenue: 14982.40 },
  { name: "OfferLab JV Link", url: "https://www.offerlab.com/jv-optin?olaff=CAC3gyP5m", workspace: "OfferLab", clicks: 2914, unique: 2218, epc: 6.14, revenue: 13620.80 },
  { name: "One Comma Club Challenge", url: "https://www.onecommaclub.com/?aff=cfc6e1d4-f9b6", workspace: "ClickFunnels 2.0", clicks: 6480, unique: 4912, epc: 3.94, revenue: 19370.40 },
  { name: "Im All In - Funnel Builder Secrets Offer", url: "https://www.imallin.com/?aff=cfc6e1d4-f9b6-48b5-8", workspace: "ClickFunnels 2.0", clicks: 3870, unique: 2940, epc: 5.21, revenue: 15197.40 },
  { name: "3 Months of ClickFunnels for $99", url: "https://www.clickfunnels.com/3-months-for-99?aff=c", workspace: "ClickFunnels 2.0", clicks: 5120, unique: 4218, epc: 2.18, revenue: 11164.80 },
  { name: "PLR Funnels", url: "https://www.plrfunnels.com/plr-wf?aff=cfc6e1d4-f9b6", workspace: "ClickFunnels 2.0", clicks: 2418, unique: 1890, epc: 3.42, revenue: 8260.80 },
  { name: "One Funnel Away Challenge", url: "https://www.onefunnelaway.com/?aff=cfc6e1d4-f9b6", workspace: "ClickFunnels Classic", clicks: 3914, unique: 3018, epc: 2.94, revenue: 11507.20 },
  { name: "ClickFunnels Free Trial", url: "https://www.clickfunnels.com/signup-flow?aff=cfc6e1", workspace: "ClickFunnels Classic", clicks: 4218, unique: 3490, epc: 1.84, revenue: 7761.20 },
  { name: "30 Days Book", url: "https://www.30days.com/?aff=084631a341e97167c9c", workspace: "Prime Mover", clicks: 2840, unique: 2218, epc: 1.42, revenue: 4032.80 },
  { name: "Expert Secrets Book", url: "https://www.expertsecrets.com/?aff=084631a341e971", workspace: "Prime Mover", clicks: 3914, unique: 3120, epc: 1.68, revenue: 6575.60 },
  { name: "DotCom Secrets Book", url: "https://www.dotcomsecrets.com/?aff=084631a341e97", workspace: "Prime Mover", clicks: 3214, unique: 2640, epc: 1.94, revenue: 6235.20 },
  { name: "Selling Online", url: "https://www.sellingonline.com/?aff=084631a341e9716", workspace: "Prime Mover", clicks: 1820, unique: 1480, epc: 2.84, revenue: 5168.80 },
  { name: "Affiliate Sign Up - 2nd Tier Commissions", url: "https://www.primemover.com/affiliate?aff=084631a34", workspace: "Prime Mover", clicks: 4920, unique: 3814, epc: 4.18, revenue: 20565.60 },
];

const SWIPE_CATEGORIES = [
  { name: "Email Swipes", count: 6 },
  { name: "Social Media Posts", count: 4 },
  { name: "Documents", count: 2 },
  { name: "Ad Copy", count: 3 },
  { name: "Sales Page Elements", count: 2 },
  { name: "Pitch Scripts", count: 2 },
  { name: "Outreach", count: 3 },
];

type Swipe = {
  title: string;
  category: string;
  icon: ComponentType<{ className?: string }>;
  preview: string;
  tags: string[];
  views: number;
  copies: number;
};

const SWIPES: Swipe[] = [
  {
    title: "Last-Chance Reminder Email",
    category: "Email Swipes",
    icon: Mail,
    preview: `"Closing tonight, don't miss this"\nHey {{first_name}},\n\nQuick heads up, the doors close on\n{{program_name}} tonight at midnight…`,
    tags: ["urgency", "last-chance", "launch"],
    views: 18,
    copies: 6,
  },
  {
    title: "Soft Pre-Launch Tease",
    category: "Email Swipes",
    icon: Mail,
    preview: `"Something I've been quietly testing"\nHey {{first_name}},\n\nFor the last 60 days I've been running a\nquiet experiment…`,
    tags: ["pre-launch", "tease", "curiosity"],
    views: 14,
    copies: 4,
  },
  {
    title: "High-CTR Facebook Ad",
    category: "Ad Copy",
    icon: Megaphone,
    preview: `Stop scrolling.\n\nIf you've tried 3+ "make money online" things\nand felt like the system was rigged, this i…`,
    tags: ["facebook", "cold-traffic", "direct-response"],
    views: 22,
    copies: 9,
  },
  {
    title: "Bonus Stack Reveal Block",
    category: "Sales Page Elements",
    icon: Gift,
    preview: `When you join {{program_name}} through my\nlink, you also get my complete bonus stack:\n\n🎁 BONUS #1, {{bonus_1_name}} ($297 value)…`,
    tags: ["bonus-stack", "sales-page", "value-stack"],
    views: 11,
    copies: 5,
  },
  {
    title: "60-Second Reels/Shorts Hook",
    category: "Social Media Posts",
    icon: Mic,
    preview: `[HOOK 0-3s]\n"Most affiliates think you need a huge list\nto make this work. You don't. Here's what\nactually moves the needle."…`,
    tags: ["video", "reels", "shorts"],
    views: 16,
    copies: 7,
  },
  {
    title: "Cold DM Outreach Template",
    category: "Outreach",
    icon: Send,
    preview: `Hey {{first_name}}, saw your post about\n{{topic}}, really resonated.\n\nNot pitching anything, just curious, are yo…`,
    tags: ["dm", "outreach", "cold"],
    views: 9,
    copies: 3,
  },
  {
    title: "Results Screenshot Post",
    category: "Social Media Posts",
    icon: Mic,
    preview: `90 days ago I bet on {{program_name}}.\n\nHere's what the results look like today:\n[screenshot]…`,
    tags: ["proof", "results", "testimonial"],
    views: 24,
    copies: 11,
  },
  {
    title: "Webinar Pitch Script Opener",
    category: "Pitch Scripts",
    icon: Mic,
    preview: `Before I show you exactly how to grab\n{{program_name}}, I want to be honest about\nwhat this is and who it's NOT for…`,
    tags: ["webinar", "pitch", "high-ticket"],
    views: 7,
    copies: 2,
  },
];

const BONUS_PROJECTS = [
  {
    name: "Skool",
    type: "Sales Copy",
    bonusCount: 5,
    target: "$5,000",
  },
  {
    name: "ClickFunnels 2.0 Launch",
    type: "Launch Stack",
    bonusCount: 7,
    target: "$8,500",
  },
  {
    name: "Prime Mover JV",
    type: "JV Promo",
    bonusCount: 4,
    target: "$3,200",
  },
  {
    name: "OfferLab Founders",
    type: "Founder Bonus",
    bonusCount: 6,
    target: "$6,000",
  },
];

/* ============================================================
 * COMPONENT
 * ============================================================ */

type ViewKey =
  | "command"
  | "people"
  | "anomalies"
  | "digests"
  | "training"
  | "centers"
  | "swipes"
  | "calendar"
  | "bonus"
  | WorkspaceKey;

function DemoPage() {
  const [view, setView] = useState<ViewKey>("command");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { resolved, setTheme } = useTheme();

  const noop = (label = "Demo mode") => () =>
    toast(label, { description: "This is a read-only product tour. Start a free trial to use the live tools." });

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      {/* Top banner */}
      <div className="flex items-center justify-center gap-3 border-b border-border bg-primary/10 px-4 py-2 text-xs text-foreground/90">
        <Eye className="h-3.5 w-3.5 text-primary" />
        <span>
          You're inside the <span className="font-semibold">Super Affiliate OS</span> live demo. All
          actions are disabled, explore freely.
        </span>
      </div>

      {/* App shell */}
      <div className="flex flex-1">
        <DemoSidebar
          current={view}
          onSelect={(v) => { setView(v); setSidebarOpen(false); }}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main className="min-h-[calc(100vh-37px)] flex-1">
          {/* Top bar */}
          <header className="flex items-center justify-between border-b border-border bg-background/80 px-4 py-2 backdrop-blur">
            <button
              type="button"
              onClick={() => setSidebarOpen((v) => !v)}
              className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
              aria-label="Toggle sidebar"
            >
              <SidebarIcon className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                aria-label="Toggle theme"
              >
                {resolved === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button onClick={noop("Notifications")} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent">
                <Bell className="h-4 w-4" />
              </button>
              <span className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/20 text-[11px] font-semibold text-primary">
                DB
              </span>
            </div>
          </header>

          <div className="px-6 py-6">
            {view === "command" && <CommandCenterView noop={noop} />}
            {view === "people" && <GlobalPeopleView noop={noop} />}
            {view === "anomalies" && <AnomaliesView noop={noop} />}
            {view === "digests" && <DigestsView noop={noop} />}
            {view === "training" && <TrainingView noop={noop} />}
            {view === "centers" && <CentersView noop={noop} />}
            {view === "swipes" && <SwipesView noop={noop} />}
            {view === "calendar" && <CalendarView noop={noop} />}
            {view === "bonus" && <BonusBuilderView noop={noop} />}
            {(view === "prime_mover" ||
              view === "cf2" ||
              view === "cf_classic" ||
              view === "offerlab") && (
              <WorkspaceView workspaceKey={view as WorkspaceKey} noop={noop} />
            )}

            {/* CTAs */}
            <div className="mt-16 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-background to-background p-8 text-center">
              <h3 className="text-2xl font-semibold tracking-tight">Like what you see?</h3>
              <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
                This is the same product you'd use day one. 14-day free trial, no charge until day 15,
                cancel anytime.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Button size="lg" asChild>
                  <Link to="/signup">
                    Start 14-day free trial <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/pricing">See pricing</Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

/* ============================================================
 * SIDEBAR
 * ============================================================ */

function DemoSidebar({
  current,
  onSelect,
  open,
  onClose,
}: {
  current: ViewKey;
  onSelect: (v: ViewKey) => void;
  open: boolean;
  onClose: () => void;
}) {
  const sections: Array<{
    label: string;
    items: Array<{ key: ViewKey; label: string; icon: ComponentType<{ className?: string }> }>;
  }> = [
    {
      label: "Overview",
      items: [
        { key: "command", label: "Command Center", icon: LayoutDashboard },
        { key: "people", label: "People", icon: Users },
        { key: "anomalies", label: "Anomalies", icon: AlertTriangle },
        { key: "digests", label: "Digests", icon: Mail },
      ],
    },
    {
      label: "Resources",
      items: [
        { key: "training", label: "Training", icon: GraduationCap },
        { key: "centers", label: "Affiliate Centers", icon: Link2 },
        { key: "swipes", label: "Swipe Files", icon: FileText },
        { key: "calendar", label: "Calendar", icon: CalendarIcon },
        { key: "bonus", label: "Bonus Builder", icon: Gift },
      ],
    },
    {
      label: "Workspaces",
      items: [
        { key: "prime_mover", label: "Prime Mover", icon: Folder },
        { key: "cf2", label: "ClickFunnels 2.0", icon: Folder },
        { key: "cf_classic", label: "ClickFunnels Classic", icon: Folder },
        { key: "offerlab", label: "OfferLab", icon: Folder },
      ],
    },
  ];

  if (!open) return null;

  const content = (
    <>
      <div className="flex h-14 items-center justify-between px-4">
        <Link to="/" className="flex items-center">
          <Wordmark />
        </Link>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent lg:hidden"
          aria-label="Close sidebar"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <nav className="space-y-6 px-3 pb-8">
        {sections.map((sec) => (
          <div key={sec.label}>
            <div className="px-2 pb-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
              {sec.label}
            </div>
            <ul className="space-y-0.5">
              {sec.items.map((item) => {
                const Icon = item.icon;
                const active = current === item.key;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => onSelect(item.key)}
                      className={cn(
                        "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
                        active
                          ? "bg-accent text-foreground"
                          : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
    </>
  );

  return (
    <>
      {/* Mobile overlay */}
      <div
        className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm lg:hidden"
        onClick={onClose}
      />
      <aside className="fixed inset-y-0 left-0 z-50 w-60 overflow-y-auto border-r border-border bg-card lg:static lg:z-auto lg:block lg:bg-card/40">
        {content}
      </aside>
    </>
  );
}

/* ============================================================
 * PAGE HEADER
 * ============================================================ */

function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  icon: Icon,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon?: ComponentType<{ className?: string }>;
  tone?: "default" | "warn" | "danger" | "success";
}) {
  const ring =
    tone === "warn"
      ? "border-warning/40"
      : tone === "danger"
        ? "border-destructive/40"
        : tone === "success"
          ? "border-primary/40"
          : "border-border";
  return (
    <div className={cn("rounded-xl border bg-card p-4", ring)}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-2 font-mono font-tnum text-2xl font-semibold">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

/* ============================================================
 * COMMAND CENTER
 * ============================================================ */

function CommandCenterView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <>
      <PageHeader
        title="Command Center"
        subtitle="All your programs, one view."
        right={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-sm">
              <button onClick={noop()} className="rounded px-3 py-1 font-medium">
                Dashboard
              </button>
              <button onClick={noop()} className="rounded px-3 py-1 text-muted-foreground">
                Comparison
              </button>
            </div>
            <select
              onChange={noop()}
              className="h-9 rounded-md border border-border bg-card px-3 text-sm"
              defaultValue="all"
            >
              <option value="all">All Programs</option>
            </select>
            <select
              onChange={noop()}
              className="h-9 rounded-md border border-border bg-card px-3 text-sm"
              defaultValue="month"
            >
              <option value="month">This Month</option>
            </select>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <MetricCard
          label="Total Revenue"
          value={`$${TOTAL.revenue.toLocaleString()}`}
          hint={<span className="text-primary">↑ 24.7% vs prev</span>}
          icon={DollarSign}
        />
        <MetricCard label="MRR" value={`$${TOTAL.mrr.toLocaleString()}`} icon={TrendingUp} />
        <MetricCard label="One-Time Revenue" value={`$${TOTAL.oneTime.toLocaleString()}`} icon={DollarSign} />
        <MetricCard
          label="Active Customers"
          value={DEDUPED_ACTIVE.toLocaleString()}
          hint="Deduped across programs"
          icon={Users}
        />
        <MetricCard label="Churn Rate" value={`${CHURN_RATE}%`} icon={Info} />
        <MetricCard
          label="Net New Revenue"
          value={`$${NET_NEW_REVENUE.toLocaleString()}`}
          hint="vs previous period"
          icon={TrendingUp}
        />
      </div>

      <h2 className="mt-8 mb-3 text-sm font-semibold">By Program</h2>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {WORKSPACES.map((w) => (
          <div key={w.key} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <Folder className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{w.name}</span>
            </div>
            <div className="mt-3 font-mono font-tnum text-2xl font-semibold">
              ${w.revenue.toLocaleString()}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              MRR ${w.mrr.toLocaleString()} · {w.activeMonthly} active · +{w.newCustomers} new
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card lg:col-span-2">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold">Activity</div>
          <ul className="max-h-[420px] divide-y divide-border overflow-auto">
            {[
              ["OfferLab", "5/19/2026", "Products configuration saved, 8 tiers, 42 contacts updated"],
              ["Prime Mover", "5/19/2026", "Reconciliation: 487 active, 38 trials, 64 upgraded, 12 churned, 0 cancelled, 47 2nd tier"],
              ["ClickFunnels 2.0", "5/19/2026", "AI digest generated, daily summary across 4 programs"],
              ["OfferLab", "5/18/2026", "False-churn detection: 18 cohort flagged for review"],
              ["Prime Mover", "5/18/2026", "Reconciliation: 481 active, 36 trials, 61 upgraded, 11 churned, 0 cancelled"],
              ["ClickFunnels Classic", "5/18/2026", "CSV import: 740 rows processed, 0 errors"],
              ["ClickFunnels 2.0", "5/17/2026", "Anomaly resolved, Tier Downgrade (manual)"],
              ["OfferLab", "5/17/2026", "Products configuration saved, 8 tiers, 40 contacts updated"],
              ["Prime Mover", "5/17/2026", "Reconciliation: 478 active, 35 trials, 58 upgraded, 9 churned"],
            ].map((row, i) => (
              <li key={i} className="flex items-start gap-3 px-4 py-3 text-xs">
                <Badge variant="outline" className="shrink-0">
                  {row[0]}
                </Badge>
                <span className="shrink-0 text-muted-foreground">{row[1]}</span>
                <span className="text-foreground/90">{row[2]}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-border bg-card">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold">Top Customers</div>
          <ul className="divide-y divide-border">
            {TOP_CUSTOMERS.map((c) => (
              <li key={c.email} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{c.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{c.program}</div>
                </div>
                <span className="font-mono font-tnum text-sm">${c.ltv.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}

/* ============================================================
 * GLOBAL PEOPLE
 * ============================================================ */

function GlobalPeopleView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Users className="h-5 w-5" /> Global People
          </span>
        }
        subtitle="Customers deduplicated across every program. Your real LTV, not per-program guesses."
        right={
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Total Unique People
            </div>
            <div className="font-mono font-tnum text-2xl font-semibold">
              {TOTAL_PEOPLE.toLocaleString()}
            </div>
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by email or name..."
              className="pl-9"
              onChange={noop("Search")}
            />
          </div>
          <select
            onChange={noop()}
            defaultValue="ltv"
            className="h-9 rounded-md border border-border bg-card px-3 text-sm"
          >
            <option value="ltv">Highest LTV</option>
            <option value="recent">Most Recent</option>
            <option value="risk">Churn Risk</option>
          </select>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Programs</th>
              <th className="px-4 py-3 text-right">Total LTV</th>
              <th className="px-4 py-3">Last Activity</th>
              <th className="px-4 py-3">Churn Risk</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {GLOBAL_PEOPLE.map((p) => (
              <tr key={p.email} className="border-b border-border last:border-b-0">
                <td className="px-4 py-2.5">
                  <div className="font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.email}</div>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {p.programs.map((prog) => (
                      <Badge key={prog} variant="secondary" className="text-[10px]">
                        {prog}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-right font-mono font-tnum">
                  ${p.ltv.toLocaleString()}
                </td>
                <td className="px-4 py-2.5 text-xs text-muted-foreground">{p.last}</td>
                <td className="px-4 py-2.5">
                  <Badge
                    variant="outline"
                    className={cn(
                      "text-[10px] capitalize",
                      p.risk === "high" && "border-destructive/50 text-destructive",
                      p.risk === "medium" && "border-warning/50 text-warning",
                      p.risk === "low" && "border-border text-muted-foreground",
                    )}
                  >
                    {p.risk}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" variant="ghost" onClick={noop("Explain LTV")}>
                    <Sparkles className="mr-1 h-3 w-3" /> Explain
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ============================================================
 * ANOMALIES
 * ============================================================ */

function AnomaliesView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <>
      <PageHeader title="Anomaly Inbox" subtitle="Issues across every workspace, in one place." />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search title, body, type..." className="pl-9" onChange={noop("Search")} />
        </div>
        <select onChange={noop()} defaultValue="open" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
          <option value="open">Open</option>
        </select>
        <select onChange={noop()} defaultValue="all" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
          <option value="all">All severities</option>
        </select>
        <select onChange={noop()} defaultValue="30" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
          <option value="30">Last 30 days</option>
        </select>
        <Button variant="outline" size="sm" onClick={noop("Run scan")}>
          Run scan
        </Button>
      </div>

      <ul className="space-y-3">
        {ANOMALIES.map((a, i) => {
          const sevColor =
            a.severity === "critical"
              ? "border-l-destructive bg-destructive/5"
              : a.severity === "high"
                ? "border-l-warning bg-warning/5"
                : a.severity === "low"
                  ? "border-l-primary/40 bg-primary/5"
                  : "border-l-warning/60 bg-card";
          return (
            <li
              key={i}
              className={cn(
                "flex items-start gap-3 rounded-xl border border-border border-l-4 p-4",
                sevColor,
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      "capitalize",
                      a.severity === "critical" && "border-destructive/50 bg-destructive/10 text-destructive",
                      a.severity === "high" && "border-warning/50 bg-warning/10 text-warning",
                      a.severity === "medium" && "border-warning/40 bg-warning/5 text-warning",
                      a.severity === "low" && "border-primary/40 bg-primary/10 text-primary",
                    )}
                  >
                    {a.severity}
                  </Badge>
                  <Badge variant="secondary" className="text-[10px]">{a.workspace}</Badge>
                  <span className="text-xs text-muted-foreground">{a.type}</span>
                  <span className="text-xs text-muted-foreground">· {a.contacts} contacts</span>
                  <span className="text-xs text-muted-foreground">· {a.age}</span>
                </div>
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="font-medium leading-snug">{a.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{a.body}</p>
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={noop("View anomaly")}>
                <Eye className="mr-1 h-3.5 w-3.5" /> View
              </Button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/* ============================================================
 * DIGESTS
 * ============================================================ */

function DigestsView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <>
      <PageHeader
        title="Smart Digests"
        subtitle="Periodic briefs across every workspace. Open anomalies, MRR snapshot, recently resolved."
        right={
          <div className="flex items-center gap-2">
            <select onChange={noop()} defaultValue="daily" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <Button onClick={noop("Generate digest")}>
              <Sparkles className="mr-1.5 h-4 w-4" /> Generate digest
            </Button>
          </div>
        }
      />

      <div className="space-y-3">
        {DIGESTS.map((d, i) => (
          <button
            key={i}
            type="button"
            onClick={noop("Open digest")}
            className="flex w-full items-start gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-accent/40"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">{d.period}</Badge>
                <span className="text-xs text-muted-foreground">{d.age}</span>
              </div>
              <p className="mt-1.5 font-medium leading-snug">{d.headline}</p>
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{d.body}</p>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

/* ============================================================
 * TRAINING
 * ============================================================ */

function TrainingView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <div className="mx-auto max-w-5xl sm:px-6 lg:px-10">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <GraduationCap className="h-5 w-5" /> Training Library
          </span>
        }
        subtitle="Foundational courses and your account's training content."
      />

      <section className="mb-10">
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h2 className="text-base font-semibold">Super Affiliate OS Library</h2>
            <p className="text-sm text-muted-foreground">Curated training from the Super Affiliate OS team</p>
          </div>
          <Button variant="outline" onClick={noop("New course")}>
            <Plus className="mr-1 h-4 w-4" /> New course
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TRAINING_LIBRARY.map((c) => (
            <CourseCard key={c.title} course={c} onClick={noop("Open course")} />
          ))}
        </div>
      </section>

      <section>
        <div className="mb-4 flex items-end justify-between">
          <div>
            <h2 className="text-base font-semibold">My Training</h2>
            <p className="text-sm text-muted-foreground">Custom training for your account</p>
          </div>
          <Button variant="outline" onClick={noop("New course")}>
            <Plus className="mr-1 h-4 w-4" /> New course
          </Button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {MY_TRAINING.map((c) => (
            <CourseCard key={c.title} course={c} onClick={noop("Open course")} />
          ))}
        </div>
      </section>
    </div>
  );
}

function CourseCard({
  course,
  onClick,
}: {
  course: { title: string; blurb: string; lessons: number; completed: number; accent: string; icon: ComponentType<{ className?: string }> };
  onClick: () => void;
}) {
  const pct = Math.round((course.completed / Math.max(course.lessons, 1)) * 100);
  return (
    <button
      type="button"
      onClick={onClick}
      className="group overflow-hidden rounded-xl border border-border bg-card text-left transition-all hover:border-foreground/30 hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={getCourseCover(course.title)}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03] [filter:hue-rotate(110deg)] dark:[filter:none]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-card/80 via-card/20 to-transparent" />
      </div>
      <div className="p-4">
        <div className="font-medium tracking-tight">{course.title}</div>
        <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{course.blurb}</p>
        <div className="mt-3 text-xs text-muted-foreground">
          {course.completed} / {course.lessons} lessons
        </div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-border">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </button>
  );
}

/* ============================================================
 * AFFILIATE CENTERS + PROMO LINKS
 * ============================================================ */

function CentersView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <>
      <PageHeader title="Link Vault" subtitle="Affiliate centers, promotional links, and click attribution." />

      <section className="mb-10">
        <div className="mb-3 flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Affiliate Centers</h2>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search centers..." className="pl-9" onChange={noop("Search")} />
          </div>
          <select onChange={noop()} defaultValue="all" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
            <option value="all">All workspaces</option>
          </select>
          <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-sm">
            <button onClick={noop()} className="rounded px-3 py-1 font-medium">Active</button>
            <button onClick={noop()} className="rounded px-3 py-1 text-muted-foreground">Archived</button>
          </div>
          <Button onClick={noop("Add center")}>
            <Plus className="mr-1 h-4 w-4" /> Add center
          </Button>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          {AFFILIATE_CENTERS.map((c) => (
            <div key={c.name} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background overflow-hidden">
                  <img src={c.logo} alt="" loading="lazy" className="h-7 w-7 object-contain" />
                </div>
                <div className="min-w-0">
                  <div className="truncate font-medium">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.workspace}</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>{c.opens.toLocaleString()} opens</span>
                <div className="flex gap-1">
                  <IconBtn onClick={noop()} icon={Pencil} />
                  <IconBtn onClick={noop()} icon={Archive} />
                  <IconBtn onClick={noop()} icon={Trash2} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="mb-3 flex items-center gap-2">
          <Link2 className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-base font-semibold">Promotional Links</h2>
        </div>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search links..." className="pl-9" onChange={noop("Search")} />
          </div>
          <select onChange={noop()} defaultValue="all" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
            <option value="all">All workspaces</option>
          </select>
          <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-sm">
            <button onClick={noop()} className="rounded px-3 py-1 font-medium">Active</button>
            <button onClick={noop()} className="rounded px-3 py-1 text-muted-foreground">Archived</button>
          </div>
          <Button onClick={noop("New link")}>
            <Plus className="mr-1 h-4 w-4" /> New link
          </Button>
        </div>
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Link</th>
                <th className="px-4 py-3">Workspace</th>
                <th className="px-4 py-3 text-right">Clicks</th>
                <th className="px-4 py-3 text-right">Unique</th>
                <th className="px-4 py-3 text-right">EPC</th>
                <th className="px-4 py-3 text-right">Revenue</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {PROMO_LINKS.map((l) => (
                <tr key={l.name} className="border-b border-border last:border-b-0">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{l.name}</div>
                    <div className="truncate text-xs text-muted-foreground">{l.url}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{l.workspace}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-tnum">{l.clicks.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-tnum">{l.unique.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-tnum">${l.epc.toFixed(2)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-tnum">${l.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <IconBtn onClick={noop("Copy")} icon={Copy} />
                      <IconBtn onClick={noop("Stats")} icon={BarChart3} />
                      <IconBtn onClick={noop()} icon={Pencil} />
                      <IconBtn onClick={noop()} icon={Archive} />
                      <IconBtn onClick={noop()} icon={Trash2} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function IconBtn({ onClick, icon: Icon }: { onClick: () => void; icon: ComponentType<{ className?: string }> }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

/* ============================================================
 * SWIPE FILES
 * ============================================================ */

function SwipesView({ noop }: { noop: (label?: string) => () => void }) {
  const [active, setActive] = useState<string>("All swipes");
  const filtered = useMemo(
    () => (active === "All swipes" ? SWIPES : SWIPES.filter((s) => s.category === active)),
    [active],
  );
  return (
    <>
      <div className="flex gap-6">
        {/* Sub-sidebar */}
        <div className="hidden w-60 shrink-0 rounded-xl border border-border bg-card p-3 lg:block">
          <div className="px-2 pb-3">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" /> Swipe Files
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">Winning copy, ready to remix</div>
          </div>
          <button
            type="button"
            onClick={() => setActive("All swipes")}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm",
              active === "All swipes" ? "bg-primary text-primary-foreground" : "hover:bg-accent",
            )}
          >
            <span>All swipes</span>
            <span className="text-xs opacity-70">{SWIPES.length}</span>
          </button>
          <div className="mt-4 px-2 pb-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            Foundation
          </div>
          {SWIPE_CATEGORIES.map((cat) => (
            <button
              key={cat.name}
              type="button"
              onClick={() => setActive(cat.name)}
              className={cn(
                "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm",
                active === cat.name ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50",
              )}
            >
              <span>{cat.name}</span>
              <span className="text-xs opacity-70">{cat.count}</span>
            </button>
          ))}
        </div>

        {/* Main */}
        <div className="min-w-0 flex-1">
          <PageHeader
            title={active}
            subtitle={`${filtered.length} swipes`}
            right={
              <Button onClick={noop("New swipe")}>
                <Plus className="mr-1 h-4 w-4" /> New swipe
              </Button>
            }
          />
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search title, body, use case..." className="pl-9" onChange={noop("Search")} />
            </div>
            <select onChange={noop()} defaultValue="all" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
              <option value="all">All types</option>
            </select>
            <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-sm">
              <button onClick={noop()} className="rounded px-3 py-1 font-medium">All</button>
              <button onClick={noop()} className="rounded px-3 py-1 text-muted-foreground">Foundation</button>
              <button onClick={noop()} className="rounded px-3 py-1 text-muted-foreground">Mine</button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.title}
                  className="flex flex-col rounded-xl border border-border bg-card p-4"
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="font-medium leading-tight">{s.title}</span>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      Foundation
                    </Badge>
                  </div>
                  <pre className="mb-3 whitespace-pre-wrap font-sans text-xs leading-relaxed text-muted-foreground line-clamp-5">
                    {s.preview}
                  </pre>
                  <div className="mb-3 flex flex-wrap gap-1">
                    {s.tags.map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-auto flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3 w-3" /> {s.views}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Copy className="h-3 w-3" /> {s.copies}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

/* ============================================================
 * CALENDAR
 * ============================================================ */

function CalendarView({ noop }: { noop: (label?: string) => () => void }) {
  // May 2026, May 1 is Friday. Build a 6-week grid starting Sunday Apr 26.
  const start = new Date(2026, 3, 26); // April 26 2026
  const days: Array<{ date: Date; inMonth: boolean }> = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push({ date: d, inMonth: d.getMonth() === 4 });
  }
  // Events
  type Ev = { day: number; month: number; label: string; time?: string; tone: string };
  const events: Ev[] = [
    { day: 5, month: 4, label: "OfferLab Webinar", time: "11:00", tone: "bg-sky-500/30 text-sky-400" },
    { day: 12, month: 4, label: "Prime Mover Launch", time: "09:00", tone: "bg-emerald-500/30 text-emerald-400" },
    { day: 17, month: 4, label: "Bonus Stack Drop", time: "03:46", tone: "bg-violet-500/30 text-violet-400" },
    { day: 19, month: 4, label: "Today", tone: "bg-primary text-primary-foreground" },
    { day: 22, month: 4, label: "CF Classic JV Email", time: "08:00", tone: "bg-amber-500/30 text-amber-400" },
    // Marketing Secrets AI Challenge: Last Monday of May 2026 = May 25, ending Friday May 29.
    { day: 25, month: 4, label: "Marketing Secrets AI Challenge", tone: "bg-rose-500/40 text-rose-300" },
    { day: 26, month: 4, label: "Marketing Secrets AI Challenge", tone: "bg-rose-500/40 text-rose-300" },
    { day: 27, month: 4, label: "Marketing Secrets AI Challenge", tone: "bg-rose-500/40 text-rose-300" },
    { day: 28, month: 4, label: "Marketing Secrets AI Challenge", tone: "bg-rose-500/40 text-rose-300" },
    { day: 29, month: 4, label: "Marketing Secrets AI Challenge", tone: "bg-rose-500/40 text-rose-300" },
  ];

  function eventsFor(d: Date) {
    return events.filter((e) => e.day === d.getDate() && e.month === d.getMonth());
  }

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" /> Promotion Calendar
          </span>
        }
        subtitle="Program promos, launches, and your own schedule."
        right={
          <div className="flex flex-wrap items-center gap-2">
            <IconBtn onClick={noop()} icon={ChevronRight} />
            <span className="rounded-md border border-border bg-card px-3 py-1.5 text-sm font-medium">
              May 2026
            </span>
            <IconBtn onClick={noop()} icon={ChevronRight} />
            <Button variant="outline" size="sm" onClick={noop()}>Today</Button>
            <div className="inline-flex rounded-md border border-border bg-card p-0.5 text-sm">
              <button onClick={noop()} className="rounded px-3 py-1 font-medium">Month</button>
              <button onClick={noop()} className="rounded px-3 py-1 text-muted-foreground">List</button>
            </div>
            <select onChange={noop()} defaultValue="all" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
              <option value="all">Workspaces: All</option>
            </select>
            <Button onClick={noop("New event")}>
              <Plus className="mr-1 h-4 w-4" /> New event
            </Button>
          </div>
        }
      />

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="grid grid-cols-7 border-b border-border bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="px-3 py-2">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const evs = eventsFor(d.date);
            const isToday = d.date.getDate() === 19 && d.date.getMonth() === 4;
            return (
              <div
                key={i}
                className={cn(
                  "min-h-[100px] border-b border-r border-border p-2 text-xs",
                  !d.inMonth && "bg-muted/10 text-muted-foreground/40",
                  ((i + 1) % 7 === 0) && "border-r-0",
                  i >= 35 && "border-b-0",
                )}
              >
                <div className={cn(
                  "mb-1 inline-flex h-5 w-5 items-center justify-center rounded-full",
                  isToday && "bg-primary text-primary-foreground font-semibold",
                  !isToday && d.inMonth && "font-medium",
                )}>
                  {d.date.getDate()}
                </div>
                <div className="space-y-1">
                  {evs.filter(e => e.label !== "Today").map((e, j) => (
                    <button
                      key={j}
                      type="button"
                      onClick={noop("Event")}
                      className={cn(
                        "block w-full truncate rounded px-1.5 py-0.5 text-left text-[10px] font-medium",
                        e.tone,
                      )}
                    >
                      {e.time && <span className="opacity-80">{e.time} </span>}
                      {e.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ============================================================
 * BONUS BUILDER
 * ============================================================ */

function BonusBuilderView({ noop }: { noop: (label?: string) => () => void }) {
  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Gift className="h-5 w-5" /> Bonus Builder
          </span>
        }
        subtitle="AI-powered bonus stacks for the programs you promote."
        right={
          <Button onClick={noop("New project")}>
            <Plus className="mr-1 h-4 w-4" /> New Project
          </Button>
        }
      />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {BONUS_PROJECTS.map((p) => (
          <button
            key={p.name}
            type="button"
            onClick={noop("Open project")}
            className="rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-foreground/30"
          >
            <div className="flex items-start justify-between">
              <div className="font-medium">{p.name}</div>
              <IconBtn onClick={noop()} icon={Trash2} />
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge variant="outline" className="text-[10px]">{p.type}</Badge>
              <Badge variant="outline" className="text-[10px]">{p.bonusCount} bonuses</Badge>
            </div>
            <div className="mt-3 text-xs text-muted-foreground">Target: {p.target}</div>
          </button>
        ))}
      </div>
    </>
  );
}

/* ============================================================
 * WORKSPACE
 * ============================================================ */

function WorkspaceView({
  workspaceKey,
  noop,
}: {
  workspaceKey: WorkspaceKey;
  noop: (label?: string) => () => void;
}) {
  const w = WORKSPACES.find((x) => x.key === workspaceKey)!;

  return (
    <>
      <div className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">Workspace</div>
      <h1 className="text-2xl font-semibold tracking-tight">{w.name}</h1>

      <div className="mt-4 mb-6 flex flex-wrap items-center gap-1 border-b border-border">
        {["Dashboard", "Contacts", "2nd Tier Affiliates", "Products", "Anomalies", "Imports", "Manual Entry"].map(
          (tab, i) => (
            <button
              key={tab}
              type="button"
              onClick={noop()}
              className={cn(
                "border-b-2 px-3 py-2 text-sm transition-colors",
                i === 0
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {tab}
            </button>
          ),
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="outline">Metric source: metric_definitions</Badge>
        <span className="ml-auto" />
        <select onChange={noop()} defaultValue="month" className="h-9 rounded-md border border-border bg-card px-3 text-sm">
          <option value="month">This month</option>
        </select>
      </div>

      {/* Row 1 */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Revenue"
          value={`$${w.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          hint={<span className="text-primary">↑ 18.4% vs prior</span>}
          icon={DollarSign}
        />
        <MetricCard label="MRR" value={`$${w.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={TrendingUp} />
        <MetricCard label="2nd Tier MRR" value={`$${w.secondTierMrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} icon={DollarSign} />
        <MetricCard label="Total Active" value={w.totalActive.toLocaleString()} icon={Users} />
      </div>

      {/* Row 2 */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard label="Active Monthly" value={w.activeMonthly.toLocaleString()} />
        <MetricCard label="Free Trials" value={w.freeTrials.toLocaleString()} />
        <MetricCard label="Upgrades" value={w.upgrades.toLocaleString()} />
        <MetricCard label="Upgrade Rate" value={`${w.upgradeRate.toFixed(1)}%`} />
        <MetricCard label="At Risk" value={w.atRisk.toLocaleString()} tone="warn" />
      </div>

      {/* Row 3 */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard label="Contacts" value={w.contacts.toLocaleString()} />
        <MetricCard label="2nd Tier Affiliates" value={w.secondTierAffiliates.toLocaleString()} />
        <MetricCard label="Cancelled Trial" value={w.cancelledTrials.toLocaleString()} tone="danger" />
        <MetricCard label="Trial Churned" value={w.trialChurned.toLocaleString()} tone="danger" />
        <MetricCard label="Churn Rate" value={`${w.churnRate.toFixed(2)}%`} tone="warn" />
      </div>

      {/* Charts */}
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <ChartCard title="Daily Commissions">
          <BarChart series={fakeDailySeries(w.revenue)} formatY={(v) => `$${(v / 1000).toFixed(1)}k`} />
        </ChartCard>
        <ChartCard title="Revenue By Tier">
          <BarChart
            series={[
              { label: "high_ticket", value: w.revenue * 0.62 },
              { label: "front_end", value: w.revenue * 0.18 },
              { label: "second_tier", value: w.revenue * 0.12 },
              { label: "uncategorized", value: w.revenue * 0.08 },
            ]}
            formatY={(v) => `$${(v / 1000).toFixed(0)}k`}
          />
        </ChartCard>
      </div>

      {/* Recent activity */}
      <div className="mt-6 rounded-xl border border-border bg-card">
        <div className="border-b border-border px-4 py-3 text-sm font-semibold">Recent activity</div>
        <ul className="divide-y divide-border">
          {[
            ["Products configuration saved", `${w.contacts} contacts updated`, "5/19/2026, 9:59:45 PM"],
            [
              "Reconciliation",
              `${w.activeMonthly} active, ${w.freeTrials} trials, ${w.upgrades} upgraded, ${w.atRisk} at risk, 0 cancelled`,
              "5/19/2026, 9:59:44 PM",
            ],
            ["CSV import completed", `${w.contacts} rows processed, 0 errors`, "5/18/2026, 6:12:08 AM"],
            ["Anomaly auto-resolved", "False Churn Upgrade, cohort merged", "5/17/2026, 11:42:33 PM"],
          ].map((row, i) => (
            <li key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
              <div>
                <span className="font-medium">{row[0]}</span>
                <span className="text-muted-foreground">, {row[1]}</span>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">{row[2]}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

/* ============================================================
 * CHART PRIMITIVES (CSS bars)
 * ============================================================ */

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-4 text-sm font-semibold">{title}</div>
      {children}
    </div>
  );
}

function BarChart({
  series,
  formatY,
}: {
  series: Array<{ label: string; value: number }>;
  formatY: (v: number) => string;
}) {
  const max = Math.max(...series.map((s) => s.value), 1);
  return (
    <div className="space-y-2">
      <div className="flex h-48 items-end gap-1.5">
        {series.map((s, i) => {
          const h = Math.max((s.value / max) * 100, 2);
          return (
            <div key={i} className="flex flex-1 flex-col items-center justify-end">
              <div
                className="w-full rounded-t-sm bg-primary/80 transition-all hover:bg-primary"
                style={{ height: `${h}%` }}
                title={`${s.label}: ${formatY(s.value)}`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>{formatY(0)}</span>
        <span>{formatY(max)}</span>
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        {series.length <= 6 ? (
          series.map((s, i) => (
            <span key={i} className="flex-1 truncate text-center">{s.label}</span>
          ))
        ) : (
          <>
            <span>{series[0]?.label}</span>
            <span>{series[Math.floor(series.length / 2)]?.label}</span>
            <span>{series[series.length - 1]?.label}</span>
          </>
        )}
      </div>
    </div>
  );
}

function fakeDailySeries(total: number) {
  const days = 19;
  const seed = [0.18, 0.09, 0.07, 0.05, 0.03, 0.08, 0.12, 0.02, 0.04, 0.03, 0.05, 0.02, 0.03, 0.04, 0.02, 0.03, 0.04, 0.03, 0.03];
  const sum = seed.reduce((a, b) => a + b, 0);
  return seed.slice(0, days).map((p, i) => ({
    label: `May ${i + 1}`,
    value: (total * p) / sum,
  }));
}
